import React, { useEffect, useRef, useState } from "react"
import "./confirmation-popup-demo.css"

interface ConfirmationPopupProps {
  isOpen: boolean
  title: string
  message: string
  highlightedText?: string
  primaryButtonLabel: React.ReactNode
  secondaryButtonLabel: React.ReactNode
  onPrimaryAction: () => void
  onSecondaryAction: () => void
  onClose: () => void
  isPrimaryDestructive?: boolean
  isPrimaryAction?: boolean
}

const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({
  isOpen,
  title,
  message,
  highlightedText,
  primaryButtonLabel,
  secondaryButtonLabel,
  onPrimaryAction,
  onSecondaryAction,
  onClose,
  isPrimaryDestructive = false,
  isPrimaryAction = true,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
      setTimeout(() => {
        setIsVisible(false)
      }, 300)
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [isOpen, onClose])

  const handleOutsideClick = (e: React.MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  if (!isOpen && !isVisible) return null

  const messageWithHighlight = highlightedText
    ? message.replace(highlightedText, `<span class="highlighted-text">${highlightedText}</span>`)
    : message

  return (
    <div className={`popup-overlay ${isOpen ? "popup-open" : "popup-closing"}`} onClick={handleOutsideClick}>
      <div className="popup-container" ref={popupRef}>
        <div className="popup-header">
          <h2 className="popup-title">{title}</h2>
          <button className="popup-close-button" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#64748B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="popup-content">
          <p
            className="popup-message"
            dangerouslySetInnerHTML={{
              __html: messageWithHighlight,
            }}
          ></p>
        </div>

        <div className="popup-divider"></div>

        <div className={`popup-actions ${isPrimaryDestructive ? "delete" : ""}`}>
          <button
            className={`popup-button ${isPrimaryAction ? "popup-button-secondary" : "popup-button-primary"}`}
            onClick={onSecondaryAction}
          >
            {secondaryButtonLabel}
          </button>
          <button
            className={`popup-button ${
              isPrimaryDestructive ? "popup-button-destructive" : "popup-button-primary"
            } ${!isPrimaryAction ? "popup-button-secondary" : ""}`}
            onClick={onPrimaryAction}
          >
            {primaryButtonLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

const ConfirmationPopupDemo = () => {
  const [isDeleteDetailsOpen, setIsDeleteDetailsOpen] = useState(false)
  const [isDeleteUnitOpen, setIsDeleteUnitOpen] = useState(false)
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false)

  const handleDeleteAction = () => {
    // Close the popup after action
    setIsDeleteDetailsOpen(false)
    setIsDeleteUnitOpen(false)
  }

  const handleCancelAction = () => {
    // Close the popup after action
    setIsDeleteDetailsOpen(false)
    setIsDeleteUnitOpen(false)
    setIsUnsavedChangesOpen(false)
  }

  const handleContinueWithoutSaving = () => {
    setIsUnsavedChangesOpen(false)
  }

  return (
    <div className="popup-demo-container">
      <h1>Confirmation Popup Examples</h1>

      <div className="popup-demo-buttons">
        <button className="demo-button" onClick={() => setIsDeleteDetailsOpen(true)}>
          Delete Additional Details
        </button>

        <button className="demo-button" onClick={() => setIsDeleteUnitOpen(true)}>
          Delete Unit
        </button>

        <button className="demo-button" onClick={() => setIsUnsavedChangesOpen(true)}>
          Unsaved Changes
        </button>
      </div>

      {/* Delete Additional Details Popup */}
      <ConfirmationPopup
        isOpen={isDeleteDetailsOpen}
        title="Delete Additional Details"
        message="Do you want to delete Additional Details?"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDeleteAction}
        onSecondaryAction={handleCancelAction}
        onClose={handleCancelAction}
        isPrimaryDestructive={true}
      />

      {/* Delete Unit Popup */}
      <ConfirmationPopup
        isOpen={isDeleteUnitOpen}
        title="Delete Unit"
        message="Do you want to delete this BG102?"
        highlightedText="BG102"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDeleteAction}
        onSecondaryAction={handleCancelAction}
        onClose={handleCancelAction}
        isPrimaryDestructive={true}
      />

      {/* Unsaved Changes Popup */}
      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={handleCancelAction}
        onSecondaryAction={handleContinueWithoutSaving}
        onClose={handleCancelAction}
        isPrimaryAction={false}
      />
    </div>
  )
}

export { ConfirmationPopup, ConfirmationPopupDemo }